import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, PenSquare } from 'lucide-react';
import { useRegion } from '../../hooks/useRegion';
import { API_URL } from '../../lib/api';
import { cn, getStatusColor } from '../../lib/utils';
import { formatCurrency, formatRegionalDate, getBusinessNumberLabel, getRegionConfig } from '../../lib/regionConfig';
import { signInProviderLabel } from '../../lib/signInProviders';
import { formatMessageTime, planName, statusClass, statusLabel, toastApiError } from '../../lib/support';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { PlanBadge } from './PlanBadge';

/**
 * One user at a glance, for support: their plan, business, how much they
 * use the app, and every conversation we've had with them. `user` is their
 * /admin/users row; the rest is fetched when it opens.
 *
 * Dates are in the viewer's region; their money in the user's own.
 */
export function UserSupportDialog({ user, onOpenChange, onMessage }) {
  const navigate = useNavigate();
  const { region: viewerRegion } = useRegion();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    setInfo(null);
    axios
      .get(`${API_URL}/admin/users/${user.id}/support-info`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` },
      })
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch((e) => {
        if (cancelled) return;
        toastApiError(e, 'Could not load this user');
        onOpenChange(false);
      });
    return () => { cancelled = true; };
  }, [user, onOpenChange]);

  const theirRegion = info?.settings.region;
  const when = (iso) => formatMessageTime(iso, viewerRegion) || 'Never';
  const date = (iso) => formatRegionalDate(iso, viewerRegion) || '-';

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-support-dialog">
        {user && (
          <>
            <DialogHeader className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
                <div className="min-w-0 space-y-1">
                  <DialogTitle className="break-words">{user.name || user.email}</DialogTitle>
                  <DialogDescription asChild>
                    <a href={`mailto:${user.email}`} className="text-clay underline underline-offset-4 break-all">{user.email}</a>
                  </DialogDescription>
                </div>
                {user.auth_type !== 'demo' && (
                  <Button className="shrink-0 sm:mr-8" onClick={() => onMessage(user)} data-testid="user-support-message">
                    <PenSquare />
                    Message
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <PlanBadge user={user} />
                {user.is_admin && <Pill className="bg-inverse text-inverse-foreground">Admin</Pill>}
              </div>
            </DialogHeader>

            {!info ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : (
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                  <Section title="account">
                    <Rows rows={[
                      ['Plan', `${planName(user.subscription_plan)}${user.subscription_status ? ` · ${user.subscription_status}` : ''}`],
                      user.subscription_status === 'trialing' && user.trial_ends_at && ['Trial ends', date(user.trial_ends_at)],
                      user.cancel_at_period_end && ['Cancels', date(user.current_period_end)],
                      ['Region', getRegionConfig(theirRegion).name || theirRegion],
                      ['Sign-in', signInProviderLabel(user.auth_type) || 'Email and password'],
                      ['Signed up', date(user.created_at)],
                      ['Last sign-in', when(user.last_login_at)],
                    ]} />
                  </Section>
                  <Section title="business">
                    <Rows rows={[
                      ['Name', info.settings.business_name],
                      ['Email', info.settings.business_email],
                      ['Phone', info.settings.business_phone],
                      [getBusinessNumberLabel(theirRegion), info.settings.business_number],
                      ['Address', info.settings.business_address],
                    ]} />
                  </Section>
                </div>

                <Section title="usage">
                  <div className="grid grid-cols-3 gap-3">
                    <Figure label="Jobs" value={info.statistics.jobs_count} />
                    <Figure label="Equipment" value={info.statistics.equipment_count} />
                    <Figure label="Value of all jobs" value={formatCurrency(info.statistics.total_revenue, theirRegion)} />
                  </div>
                  {Object.keys(info.statistics.status_breakdown).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {Object.entries(info.statistics.status_breakdown).map(([status, count]) => (
                        <Pill key={status} className={cn('capitalize', getStatusColor(status))}>
                          {status.replace('_', ' ')} <span className="tabular-nums ml-1">{count}</span>
                        </Pill>
                      ))}
                    </div>
                  )}
                </Section>

                {info.recent_activity.length > 0 && (
                  <Section title="recent jobs">
                    <ul className="text-sm divide-y divide-line">
                      {info.recent_activity.map((job) => (
                        <li key={job.id} className="flex items-center gap-3 py-2 min-w-0">
                          <span className="truncate flex-1 min-w-0">{job.project_name}</span>
                          <Pill className={cn('capitalize shrink-0', getStatusColor(job.status))}>{job.status.replace('_', ' ')}</Pill>
                          <span className="tabular-nums w-24 text-right shrink-0 hidden sm:block">{formatCurrency(job.total, theirRegion)}</span>
                          <span className="text-muted-foreground tabular-nums w-24 text-right shrink-0">{date(job.updated_at)}</span>
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}

                <Section title="conversations">
                  {info.conversations.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No conversations yet.</p>
                  ) : (
                    <ul className="text-sm divide-y divide-line" data-testid="user-support-conversations">
                      {info.conversations.map((t) => (
                        <li key={t.id}>
                          <button
                            type="button"
                            onClick={() => navigate(`/support/${t.id}`)}
                            className="w-full flex items-center gap-3 py-2 text-left min-w-0 hover:text-clay"
                          >
                            <span className={cn('truncate flex-1 min-w-0', t.unread && 'font-semibold')}>{t.subject}</span>
                            <Pill className={cn('shrink-0', statusClass(t.status))}>{statusLabel(t.status, 'admin')}</Pill>
                            <span className="text-muted-foreground tabular-nums shrink-0 hidden sm:block">
                              {formatMessageTime(t.last_message_at, viewerRegion)}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <section className="min-w-0">
      <p className="eyebrow mb-2">{title}</p>
      {children}
    </section>
  );
}

/** Label and value pairs; a falsy row is skipped, a blank value shows as a dash. */
function Rows({ rows }) {
  return (
    <dl className="text-sm divide-y divide-line">
      {rows.filter(Boolean).map(([label, value]) => (
        <div key={label} className="flex justify-between gap-4 py-2 min-w-0">
          <dt className="text-muted-foreground shrink-0">{label}</dt>
          <dd className="text-right tabular-nums break-words min-w-0">{value || '-'}</dd>
        </div>
      ))}
    </dl>
  );
}

function Figure({ label, value }) {
  return (
    <div className="rounded-cards bg-mist px-4 py-3 min-w-0">
      <p className="text-2xl font-light tracking-tight tabular-nums truncate">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function Pill({ className, children }) {
  return <span className={cn('inline-flex items-center text-xs px-2 py-0.5 rounded-pill whitespace-nowrap', className)}>{children}</span>;
}
